/**
 * Pure utility functions for attribute processing in AssetDrawer
 *
 * These functions handle:
 * - Port and protocol matching
 * - Attribute grouping by name
 * - Technique attribute extraction
 */

import { Attribute } from '@/types';

/**
 * Result of processing asset attributes
 */
export interface ProcessedAttributes {
  /** All attributes grouped by name */
  attributes: Record<string, Attribute[]>;
  /** Technique attributes (ports matched with protocols) */
  techniqueAttributes: Record<string, Attribute>;
}

/**
 * Builds groups of ports and possible protocols from attributes
 */
interface AttributeGroups {
  ports: Attribute[];
  possibleProtocols: Record<number, Attribute[]>;
  attributesByName: Record<string, Attribute[]>;
}

/**
 * Builds initial attribute groups separating ports and protocols
 *
 * @param assetAttributes - Array of attributes to process
 * @returns Object containing ports, possibleProtocols, and attributesByName
 */
function buildAttributeGroups(assetAttributes: Attribute[]): AttributeGroups {
  const ports: Attribute[] = [];
  const possibleProtocols: Record<number, Attribute[]> = {};

  const attributesByName = assetAttributes.reduce(
    (acc, attribute) => {
      const isValueNumber = !isNaN(Number(attribute.value));

      if (attribute.name === 'port' && isValueNumber) {
        ports.push(attribute);
      } else if (isValueNumber) {
        possibleProtocols[Number(attribute.value)] = [
          ...(possibleProtocols[Number(attribute.value)] || []),
          attribute,
        ];
      }
      return {
        ...acc,
        [attribute.name]: [...(acc[attribute.name] || []), attribute],
      };
    },
    {} as Record<string, Attribute[]>
  );

  return {
    ports,
    possibleProtocols,
    attributesByName,
  };
}

/**
 * Matches ports with their corresponding protocols to build technique attributes
 *
 * @param ports - Array of port attributes
 * @param possibleProtocols - Map of port numbers to protocol attributes
 * @returns Record of technique attributes keyed by attribute key
 */
function buildTechniqueAttributes(
  ports: Attribute[],
  possibleProtocols: Record<number, Attribute[]>
): Record<string, Attribute> {
  const techniqueAttributes: Record<string, Attribute> = {};

  ports.forEach(port => {
    const portValue = Number(port.value);

    if (possibleProtocols[portValue]) {
      possibleProtocols[portValue].forEach(attribute => {
        techniqueAttributes[attribute.key] = attribute;
      });
    } else {
      techniqueAttributes[port.key] = port;
    }
  });

  return techniqueAttributes;
}

/**
 * Main function to process asset attributes into grouped and technique attributes
 *
 * @param assetAttributes - Array of asset attributes to process
 * @returns Processed attributes with grouping and technique matching
 */
export function processAssetAttributes(
  assetAttributes: Attribute[]
): ProcessedAttributes {
  const { ports, possibleProtocols, attributesByName } =
    buildAttributeGroups(assetAttributes);

  const techniqueAttributes = buildTechniqueAttributes(
    ports,
    possibleProtocols
  );

  return {
    attributes: attributesByName,
    techniqueAttributes,
  };
}
